import type { Language } from 'context/LanguageContext'

/**
 * Заголовок и описание каждой публичной страницы на всех языках сайта —
 * для <title>, meta description, Open Graph и Twitter (см. Seo.tsx).
 * Ключи совпадают с полем key в routes.json.
 */
export type SeoText = { title: string; description: string }
type SeoTexts = Record<string, Record<Language, SeoText>>

const SUFFIX = ' | RealColibri'

const STATIC: SeoTexts = {
    home: {
        en: {
            title: 'RealColibri — Automated Trading for Crypto and Global Futures',
            description:
                'Automated trading robots for Binance, Bybit, OKX and CME futures via Rithmic. Read & Trade API only — your funds stay on your exchange, no withdrawal access.',
        },
        ru: {
            title: 'RealColibri — автоматическая торговля на криптобиржах и мировых фьючерсах',
            description:
                'Торговые роботы для Binance, Bybit, OKX и фьючерсов CME через Rithmic. Только API на чтение и торговлю — деньги остаются на вашей бирже, без права вывода.',
        },
        es: {
            title: 'RealColibri — trading automático en cripto y futuros globales',
            description:
                'Robots de trading automático para Binance, Bybit, OKX y futuros de CME vía Rithmic. Solo API de lectura y trading: tus fondos permanecen en tu exchange.',
        },
        fr: {
            title: 'RealColibri — trading automatisé crypto et contrats à terme mondiaux',
            description:
                'Robots de trading automatisé pour Binance, Bybit, OKX et les contrats à terme CME via Rithmic. API lecture et trading uniquement : vos fonds restent sur votre plateforme.',
        },
        de: {
            title: 'RealColibri — automatisierter Handel mit Krypto und globalen Futures',
            description:
                'Automatisierte Trading-Roboter für Binance, Bybit, OKX und CME-Futures über Rithmic. Nur Lese- und Handels-API — Ihr Geld bleibt auf Ihrer Börse.',
        },
        it: {
            title: 'RealColibri — trading automatico su cripto e futures globali',
            description:
                'Robot di trading automatico per Binance, Bybit, OKX e futures CME tramite Rithmic. Solo API di lettura e trading: i tuoi fondi restano sul tuo exchange.',
        },
        hy: {
            title: 'RealColibri — ավտոմատ առևտուր կրիպտո և համաշխարհային ֆյուչերսների շուկաներում',
            description:
                'Ավտոմատ առևտրային ռոբոտներ Binance, Bybit, OKX և CME ֆյուչերսների համար Rithmic-ի միջոցով։ Միայն կարդալու և առևտրի API՝ ձեր միջոցները մնում են ձեր բորսայում։',
        },
    },
    premium: {
        en: {
            title: 'RealColibri Premium — pay once, trade automatically forever',
            description:
                'One-time payment for lifetime access to the RealColibri trading algorithm: no additional fees, no usage limits. Packages for crypto exchanges and global futures.',
        },
        ru: {
            title: 'RealColibri Premium — один платёж, автоторговля навсегда',
            description:
                'Разовая оплата пожизненного доступа к торговому алгоритму RealColibri: без дополнительных комиссий и ограничений. Пакеты для криптобирж и мировых фьючерсов.',
        },
        es: {
            title: 'RealColibri Premium — paga una vez, opera automáticamente para siempre',
            description:
                'Pago único por acceso de por vida al algoritmo de trading RealColibri: sin comisiones adicionales ni límites de uso. Paquetes para exchanges cripto y futuros globales.',
        },
        fr: {
            title: 'RealColibri Premium — payez une fois, tradez automatiquement pour toujours',
            description:
                "Paiement unique pour un accès à vie à l'algorithme de trading RealColibri : sans frais supplémentaires ni limites d'utilisation. Offres pour plateformes crypto et contrats à terme mondiaux.",
        },
        de: {
            title: 'RealColibri Premium — einmal zahlen, für immer automatisch handeln',
            description:
                'Einmalzahlung für lebenslangen Zugang zum RealColibri-Handelsalgorithmus: keine zusätzlichen Gebühren, keine Nutzungslimits. Pakete für Krypto-Börsen und globale Futures.',
        },
        it: {
            title: 'RealColibri Premium — paghi una volta, trading automatico per sempre',
            description:
                "Pagamento unico per l'accesso a vita all'algoritmo di trading RealColibri: nessuna commissione aggiuntiva, nessun limite di utilizzo. Pacchetti per exchange cripto e futures globali.",
        },
        hy: {
            title: 'RealColibri Premium — վճարեք մեկ անգամ, առևտրեք ավտոմատ ընդմիշտ',
            description:
                'Մեկանգամյա վճարում RealColibri առևտրային ալգորիթմի ցմահ օգտագործման համար՝ առանց լրացուցիչ վճարների և սահմանափակումների։ Փաթեթներ կրիպտո բորսաների և համաշխարհային ֆյուչերսների համար։',
        },
    },
    global: {
        en: {
            title: 'Global futures markets — automated trading via Rithmic' + SUFFIX,
            description:
                'Automated futures trading on CME, EUREX, COMEX, NYMEX, SGX and more via the Rithmic datafeed. Your funds stay with your FCM; RealColibri only sends orders.',
        },
        ru: {
            title: 'Мировые фьючерсные рынки — автоторговля через Rithmic' + SUFFIX,
            description:
                'Автоматическая торговля фьючерсами на CME, EUREX, COMEX, NYMEX, SGX и других биржах через Rithmic. Средства остаются у вашего брокера (FCM), RealColibri только выставляет ордера.',
        },
        es: {
            title: 'Mercados globales de futuros — trading automático vía Rithmic' + SUFFIX,
            description:
                'Trading automático de futuros en CME, EUREX, COMEX, NYMEX, SGX y más a través de Rithmic. Tus fondos permanecen en tu FCM; RealColibri solo envía órdenes.',
        },
        fr: {
            title: 'Marchés à terme mondiaux — trading automatisé via Rithmic' + SUFFIX,
            description:
                'Trading automatisé de contrats à terme sur CME, EUREX, COMEX, NYMEX, SGX et plus via Rithmic. Vos fonds restent chez votre FCM ; RealColibri envoie seulement les ordres.',
        },
        de: {
            title: 'Globale Futures-Märkte — automatisierter Handel über Rithmic' + SUFFIX,
            description:
                'Automatisierter Futures-Handel an CME, EUREX, COMEX, NYMEX, SGX und weiteren Börsen über Rithmic. Ihr Geld bleibt bei Ihrem FCM; RealColibri sendet nur Orders.',
        },
        it: {
            title: 'Mercati futures globali — trading automatico tramite Rithmic' + SUFFIX,
            description:
                'Trading automatico di futures su CME, EUREX, COMEX, NYMEX, SGX e altri tramite Rithmic. I tuoi fondi restano presso il tuo FCM; RealColibri invia solo gli ordini.',
        },
        hy: {
            title: 'Համաշխարհային ֆյուչերսային շուկաներ — ավտոմատ առևտուր Rithmic-ով' + SUFFIX,
            description:
                'Ֆյուչերսների ավտոմատ առևտուր CME, EUREX, COMEX, NYMEX, SGX և այլ բորսաներում Rithmic-ի միջոցով։ Միջոցները մնում են ձեր FCM-ում, RealColibri-ն միայն ուղարկում է հայտեր։',
        },
    },
    crypto: {
        en: {
            title: 'Crypto futures — automated trading on Binance, Bybit and OKX' + SUFFIX,
            description:
                'Automated crypto futures trading on Binance, Bybit and OKX 24/7. Read & Trade API only, withdrawal permission never required. RCC commission only on profit.',
        },
        ru: {
            title: 'Криптофьючерсы — автоторговля на Binance, Bybit и OKX' + SUFFIX,
            description:
                'Автоматическая торговля криптофьючерсами на Binance, Bybit и OKX 24/7. Только API на чтение и торговлю, право вывода не нужно. Комиссия в RCC — только с прибыли.',
        },
        es: {
            title: 'Futuros cripto — trading automático en Binance, Bybit y OKX' + SUFFIX,
            description:
                'Trading automático de futuros cripto en Binance, Bybit y OKX 24/7. Solo API de lectura y trading, nunca se requiere permiso de retiro. Comisión en RCC solo sobre ganancias.',
        },
        fr: {
            title: 'Contrats à terme crypto — trading automatisé sur Binance, Bybit et OKX' + SUFFIX,
            description:
                'Trading automatisé de contrats à terme crypto sur Binance, Bybit et OKX 24h/24. API lecture et trading uniquement, jamais de droit de retrait. Commission en RCC uniquement sur les gains.',
        },
        de: {
            title: 'Krypto-Futures — automatisierter Handel auf Binance, Bybit und OKX' + SUFFIX,
            description:
                'Automatisierter Krypto-Futures-Handel auf Binance, Bybit und OKX rund um die Uhr. Nur Lese- und Handels-API, keine Auszahlungsrechte. RCC-Provision nur auf Gewinne.',
        },
        it: {
            title: 'Futures cripto — trading automatico su Binance, Bybit e OKX' + SUFFIX,
            description:
                'Trading automatico di futures cripto su Binance, Bybit e OKX 24/7. Solo API di lettura e trading, mai permessi di prelievo. Commissione in RCC solo sui profitti.',
        },
        hy: {
            title: 'Կրիպտո ֆյուչերսներ — ավտոմատ առևտուր Binance, Bybit և OKX բորսաներում' + SUFFIX,
            description:
                'Կրիպտո ֆյուչերսների ավտոմատ առևտուր Binance, Bybit և OKX բորսաներում 24/7։ Միայն կարդալու և առևտրի API, դուրսբերման թույլտվություն պետք չէ։ RCC միջնորդավճար միայն շահույթից։',
        },
    },
    report: {
        en: {
            title: 'Full trading report — every deal of the algorithm' + SUFFIX,
            description:
                'Every deal executed by the RealColibri algorithm on all exchanges, straight from our database: profit, commissions and win rate. Accounts are shown anonymously.',
        },
        ru: {
            title: 'Полный отчёт по торговле — все сделки алгоритма' + SUFFIX,
            description:
                'Все сделки алгоритма RealColibri на всех биржах прямо из нашей базы данных: прибыль, комиссии и доля прибыльных сделок. Счета показаны анонимно.',
        },
        es: {
            title: 'Informe completo de trading — todas las operaciones del algoritmo' + SUFFIX,
            description:
                'Todas las operaciones del algoritmo RealColibri en todos los exchanges, directamente de nuestra base de datos: ganancias, comisiones y tasa de acierto. Cuentas anónimas.',
        },
        fr: {
            title: "Rapport de trading complet — toutes les transactions de l'algorithme" + SUFFIX,
            description:
                "Toutes les transactions de l'algorithme RealColibri sur toutes les plateformes, directement depuis notre base de données : profits, commissions et taux de réussite. Comptes anonymes.",
        },
        de: {
            title: 'Vollständiger Handelsbericht — alle Trades des Algorithmus' + SUFFIX,
            description:
                'Alle Trades des RealColibri-Algorithmus auf allen Börsen direkt aus unserer Datenbank: Gewinn, Gebühren und Trefferquote. Konten werden anonym angezeigt.',
        },
        it: {
            title: "Report di trading completo — tutte le operazioni dell'algoritmo" + SUFFIX,
            description:
                "Tutte le operazioni dell'algoritmo RealColibri su tutti gli exchange, direttamente dal nostro database: profitti, commissioni e percentuale di successo. Conti anonimi.",
        },
        hy: {
            title: 'Առևտրի ամբողջական հաշվետվություն — ալգորիթմի բոլոր գործարքները' + SUFFIX,
            description:
                'RealColibri ալգորիթմի բոլոր գործարքները բոլոր բորսաներում՝ անմիջապես մեր տվյալների բազայից․ շահույթ, միջնորդավճարներ և հաջող գործարքների տոկոս։ Հաշիվները ցուցադրվում են անանուն։',
        },
    },
    comments: {
        en: {
            title: 'Reviews of RealColibri users' + SUFFIX,
            description:
                'What RealColibri users say about automated trading on crypto exchanges and global futures. Read reviews and share your own experience.',
        },
        ru: {
            title: 'Отзывы пользователей RealColibri' + SUFFIX,
            description:
                'Что пользователи RealColibri говорят об автоматической торговле на криптобиржах и мировых фьючерсах. Читайте отзывы и делитесь своим опытом.',
        },
        es: {
            title: 'Reseñas de usuarios de RealColibri' + SUFFIX,
            description:
                'Lo que dicen los usuarios de RealColibri sobre el trading automático en exchanges cripto y futuros globales. Lee reseñas y comparte tu experiencia.',
        },
        fr: {
            title: 'Avis des utilisateurs de RealColibri' + SUFFIX,
            description:
                'Ce que disent les utilisateurs de RealColibri du trading automatisé sur les plateformes crypto et les contrats à terme mondiaux. Lisez les avis et partagez votre expérience.',
        },
        de: {
            title: 'Bewertungen von RealColibri-Nutzern' + SUFFIX,
            description:
                'Was RealColibri-Nutzer über automatisierten Handel an Krypto-Börsen und globalen Futures-Märkten sagen. Lesen Sie Bewertungen und teilen Sie Ihre Erfahrung.',
        },
        it: {
            title: 'Recensioni degli utenti RealColibri' + SUFFIX,
            description:
                'Cosa dicono gli utenti di RealColibri sul trading automatico su exchange cripto e futures globali. Leggi le recensioni e condividi la tua esperienza.',
        },
        hy: {
            title: 'RealColibri օգտատերերի կարծիքներ' + SUFFIX,
            description:
                'Ինչ են ասում RealColibri-ի օգտատերերը կրիպտո բորսաներում և համաշխարհային ֆյուչերսներում ավտոմատ առևտրի մասին։ Կարդացեք կարծիքները և կիսվեք ձեր փորձով։',
        },
    },
    guidesRealcolibri: {
        en: {
            title: 'RealColibri guides — TronLink wallet, RCC token and staking' + SUFFIX,
            description:
                'Step-by-step guides: create and connect a TronLink wallet, add and get RCC tokens, stake RCC and become a partner.',
        },
        ru: {
            title: 'Инструкции RealColibri — кошелёк TronLink, токен RCC и стейкинг' + SUFFIX,
            description:
                'Пошаговые инструкции: создать и подключить кошелёк TronLink, добавить и получить токены RCC, застейкать RCC и стать партнёром.',
        },
        es: {
            title: 'Guías de RealColibri — billetera TronLink, token RCC y staking' + SUFFIX,
            description:
                'Guías paso a paso: crea y conecta una billetera TronLink, agrega y obtén tokens RCC, haz staking de RCC y conviértete en socio.',
        },
        fr: {
            title: 'Guides RealColibri — portefeuille TronLink, jeton RCC et staking' + SUFFIX,
            description:
                'Guides pas à pas : créer et connecter un portefeuille TronLink, ajouter et obtenir des jetons RCC, staker des RCC et devenir partenaire.',
        },
        de: {
            title: 'RealColibri-Anleitungen — TronLink-Wallet, RCC-Token und Staking' + SUFFIX,
            description:
                'Schritt-für-Schritt-Anleitungen: TronLink-Wallet erstellen und verbinden, RCC-Token hinzufügen und erhalten, RCC staken und Partner werden.',
        },
        it: {
            title: 'Guide RealColibri — wallet TronLink, token RCC e staking' + SUFFIX,
            description:
                'Guide passo passo: crea e collega un wallet TronLink, aggiungi e ottieni token RCC, metti in staking RCC e diventa partner.',
        },
        hy: {
            title: 'RealColibri ուղեցույցներ — TronLink դրամապանակ, RCC թոքեն և սթեյքինգ' + SUFFIX,
            description:
                'Քայլ առ քայլ ուղեցույցներ․ ստեղծեք և միացրեք TronLink դրամապանակ, ավելացրեք և ստացեք RCC թոքեններ, սթեյքինգ արեք RCC և դարձեք գործընկեր։',
        },
    },
    guidesRithmic: {
        en: {
            title: 'Rithmic guides — connect global futures to RealColibri' + SUFFIX,
            description:
                'Step-by-step guides: choose an FCM, subscribe to CME market data, get Rithmic credentials and connect them to the RealColibri trading algorithm.',
        },
        ru: {
            title: 'Инструкции по Rithmic — подключение мировых фьючерсов к RealColibri' + SUFFIX,
            description:
                'Пошаговые инструкции: выбрать FCM, подписаться на рыночные данные CME, получить логин Rithmic и подключить его к торговому алгоритму RealColibri.',
        },
        es: {
            title: 'Guías de Rithmic — conecta futuros globales a RealColibri' + SUFFIX,
            description:
                'Guías paso a paso: elige un FCM, suscríbete a los datos de mercado de CME, obtén credenciales de Rithmic y conéctalas al algoritmo de trading RealColibri.',
        },
        fr: {
            title: 'Guides Rithmic — connectez les contrats à terme mondiaux à RealColibri' + SUFFIX,
            description:
                "Guides pas à pas : choisir un FCM, s'abonner aux données de marché CME, obtenir des identifiants Rithmic et les connecter à l'algorithme de trading RealColibri.",
        },
        de: {
            title: 'Rithmic-Anleitungen — globale Futures mit RealColibri verbinden' + SUFFIX,
            description:
                'Schritt-für-Schritt-Anleitungen: FCM wählen, CME-Marktdaten abonnieren, Rithmic-Zugangsdaten erhalten und mit dem RealColibri-Handelsalgorithmus verbinden.',
        },
        it: {
            title: 'Guide Rithmic — collega i futures globali a RealColibri' + SUFFIX,
            description:
                "Guide passo passo: scegli un FCM, abbonati ai dati di mercato CME, ottieni le credenziali Rithmic e collegale all'algoritmo di trading RealColibri.",
        },
        hy: {
            title: 'Rithmic ուղեցույցներ — միացրեք համաշխարհային ֆյուչերսները RealColibri-ին' + SUFFIX,
            description:
                'Քայլ առ քայլ ուղեցույցներ․ ընտրեք FCM, բաժանորդագրվեք CME շուկայական տվյալներին, ստացեք Rithmic մուտքի տվյալներ և միացրեք դրանք RealColibri առևտրային ալգորիթմին։',
        },
    },
    staking: {
        en: {
            title: 'RCC staking — passive income from platform commissions' + SUFFIX,
            description:
                'Stake RCC and receive half of the commission paid on every trade on the platform. The longer your RCC stays locked, the bigger your share.',
        },
        ru: {
            title: 'Стейкинг RCC — пассивный доход с комиссий платформы' + SUFFIX,
            description:
                'Заблокируйте RCC в стейкинге и получайте половину комиссии с каждой сделки на платформе. Чем дольше срок блокировки, тем больше ваша доля.',
        },
        es: {
            title: 'Staking de RCC — ingresos pasivos de las comisiones' + SUFFIX,
            description:
                'Haz staking de RCC y recibe la mitad de la comisión de cada operación en la plataforma. Cuanto más tiempo bloquees tus RCC, mayor será tu parte.',
        },
        fr: {
            title: 'Staking RCC — revenus passifs issus des commissions' + SUFFIX,
            description:
                'Stakez vos RCC et recevez la moitié de la commission de chaque transaction sur la plateforme. Plus vos RCC restent bloqués longtemps, plus votre part est grande.',
        },
        de: {
            title: 'RCC-Staking — passives Einkommen aus Plattformgebühren' + SUFFIX,
            description:
                'Staken Sie RCC und erhalten Sie die Hälfte der Provision jedes Trades auf der Plattform. Je länger Ihre RCC gesperrt sind, desto größer Ihr Anteil.',
        },
        it: {
            title: 'Staking RCC — reddito passivo dalle commissioni' + SUFFIX,
            description:
                'Metti in staking i tuoi RCC e ricevi metà della commissione di ogni operazione sulla piattaforma. Più a lungo restano bloccati, maggiore è la tua quota.',
        },
        hy: {
            title: 'RCC սթեյքինգ — պասիվ եկամուտ հարթակի միջնորդավճարներից' + SUFFIX,
            description:
                'Սթեյքինգ արեք RCC և ստացեք հարթակում յուրաքանչյուր գործարքի միջնորդավճարի կեսը։ Որքան երկար են RCC-ները արգելափակված, այնքան մեծ է ձեր բաժինը։',
        },
    },
    referral: {
        en: {
            title: "Referral program — earn from your invitees' trading" + SUFFIX,
            description:
                'Every user gets a personal referral link from day one. Share it and receive a share of the commission when the people you invite trade.',
        },
        ru: {
            title: 'Реферальная программа — доход с торговли приглашённых' + SUFFIX,
            description:
                'Личная реферальная ссылка есть у каждого пользователя с первого дня. Делитесь ею и получайте часть комиссии, когда приглашённые вами торгуют.',
        },
        es: {
            title: 'Programa de referidos — gana con el trading de tus invitados' + SUFFIX,
            description:
                'Cada usuario tiene un enlace de referido personal desde el primer día. Compártelo y recibe parte de la comisión cuando operen las personas que invitas.',
        },
        fr: {
            title: 'Programme de parrainage — gagnez sur le trading de vos filleuls' + SUFFIX,
            description:
                "Chaque utilisateur dispose d'un lien de parrainage personnel dès le premier jour. Partagez-le et recevez une part de la commission lorsque vos filleuls tradent.",
        },
        de: {
            title: 'Empfehlungsprogramm — verdienen Sie am Handel Ihrer Eingeladenen' + SUFFIX,
            description:
                'Jeder Nutzer hat vom ersten Tag an einen persönlichen Empfehlungslink. Teilen Sie ihn und erhalten Sie einen Anteil der Provision, wenn Ihre Eingeladenen handeln.',
        },
        it: {
            title: 'Programma referral — guadagna dal trading dei tuoi invitati' + SUFFIX,
            description:
                'Ogni utente ha un link referral personale fin dal primo giorno. Condividilo e ricevi una parte della commissione quando le persone che inviti fanno trading.',
        },
        hy: {
            title: 'Ռեֆերալ ծրագիր — եկամուտ հրավիրվածների առևտրից' + SUFFIX,
            description:
                'Յուրաքանչյուր օգտատեր առաջին իսկ օրվանից ունի անձնական ռեֆերալ հղում։ Կիսվեք այն և ստացեք միջնորդավճարի մի մասը, երբ ձեր հրավիրածները առևտուր անեն։',
        },
    },
    partner: {
        en: {
            title: 'Partner program — VIP status with bonus earnings' + SUFFIX,
            description:
                'Partner is a VIP status above the referral program: after 100,000 RCC purchased by you or your direct referrals, get an extra 2–20% bonus on earnings from your network.',
        },
        ru: {
            title: 'Партнёрская программа — VIP-статус с бонусным доходом' + SUFFIX,
            description:
                'Партнёр — VIP-статус выше реферальной программы: после покупки 100 000 RCC вами или вашими прямыми рефералами — дополнительный бонус 2–20% с дохода вашей сети.',
        },
        es: {
            title: 'Programa de socios — estatus VIP con ganancias extra' + SUFFIX,
            description:
                'Socio es un estatus VIP por encima del programa de referidos: tras 100.000 RCC comprados por ti o tus referidos directos, obtén un bono extra del 2–20% sobre las ganancias de tu red.',
        },
        fr: {
            title: 'Programme partenaire — statut VIP avec bonus' + SUFFIX,
            description:
                'Partenaire est un statut VIP au-dessus du parrainage : après 100 000 RCC achetés par vous ou vos filleuls directs, recevez un bonus de 2 à 20 % sur les gains de votre réseau.',
        },
        de: {
            title: 'Partnerprogramm — VIP-Status mit Bonuseinnahmen' + SUFFIX,
            description:
                'Partner ist ein VIP-Status über dem Empfehlungsprogramm: Nach 100.000 gekauften RCC (von Ihnen oder direkten Empfehlungen) erhalten Sie 2–20 % Bonus auf die Einnahmen Ihres Netzwerks.',
        },
        it: {
            title: 'Programma partner — status VIP con bonus' + SUFFIX,
            description:
                'Partner è uno status VIP superiore al programma referral: dopo 100.000 RCC acquistati da te o dai tuoi referral diretti, ottieni un bonus extra del 2–20% sui guadagni della tua rete.',
        },
        hy: {
            title: 'Գործընկերային ծրագիր — VIP կարգավիճակ բոնուսային եկամտով' + SUFFIX,
            description:
                'Գործընկերը VIP կարգավիճակ է ռեֆերալ ծրագրից վեր․ ձեր կամ ձեր անմիջական ռեֆերալների կողմից 100 000 RCC գնելուց հետո ստացեք լրացուցիչ 2–20% բոնուս ձեր ցանցի եկամուտներից։',
        },
    },
    terms: {
        en: {
            title: 'Terms and Conditions' + SUFFIX,
            description:
                'Terms and conditions of use of the RealColibri website and automated trading services, including privacy policy and complaints procedure.',
        },
        ru: {
            title: 'Условия использования' + SUFFIX,
            description:
                'Условия использования сайта и сервисов автоматической торговли RealColibri, включая политику конфиденциальности и порядок подачи жалоб.',
        },
        es: {
            title: 'Términos y condiciones' + SUFFIX,
            description:
                'Términos y condiciones de uso del sitio web y los servicios de trading automático de RealColibri, incluida la política de privacidad y el procedimiento de reclamaciones.',
        },
        fr: {
            title: 'Conditions générales' + SUFFIX,
            description:
                "Conditions générales d'utilisation du site et des services de trading automatisé RealColibri, y compris la politique de confidentialité et la procédure de réclamation.",
        },
        de: {
            title: 'Allgemeine Geschäftsbedingungen' + SUFFIX,
            description:
                'Nutzungsbedingungen der RealColibri-Website und der automatisierten Handelsdienste, einschließlich Datenschutzerklärung und Beschwerdeverfahren.',
        },
        it: {
            title: 'Termini e condizioni' + SUFFIX,
            description:
                "Termini e condizioni d'uso del sito e dei servizi di trading automatico RealColibri, inclusi l'informativa sulla privacy e la procedura di reclamo.",
        },
        hy: {
            title: 'Օգտագործման պայմաններ' + SUFFIX,
            description:
                'RealColibri կայքի և ավտոմատ առևտրի ծառայությունների օգտագործման պայմանները, ներառյալ գաղտնիության քաղաքականությունը և բողոքների ընթացակարգը։',
        },
    },
}

/** Руководства по биржам — одинаковые по смыслу, отличается только биржа. */
const exchangeGuides = (ex: string): Record<Language, SeoText> => ({
    en: {
        title: `${ex} guides — connect your account to RealColibri` + SUFFIX,
        description: `Step-by-step ${ex} guides: open an account, fund the futures wallet, create API keys and connect them to the RealColibri trading algorithm.`,
    },
    ru: {
        title: `Инструкции по ${ex} — подключение счёта к RealColibri` + SUFFIX,
        description: `Пошаговые инструкции по ${ex}: открыть счёт, пополнить фьючерсный кошелёк, создать API-ключи и подключить их к торговому алгоритму RealColibri.`,
    },
    es: {
        title: `Guías de ${ex} — conecta tu cuenta a RealColibri` + SUFFIX,
        description: `Guías paso a paso de ${ex}: abre una cuenta, fondea la billetera de futuros, crea claves API y conéctalas al algoritmo de trading RealColibri.`,
    },
    fr: {
        title: `Guides ${ex} — connectez votre compte à RealColibri` + SUFFIX,
        description: `Guides pas à pas pour ${ex} : ouvrir un compte, alimenter le portefeuille futures, créer des clés API et les connecter à l'algorithme de trading RealColibri.`,
    },
    de: {
        title: `${ex}-Anleitungen — Konto mit RealColibri verbinden` + SUFFIX,
        description: `Schritt-für-Schritt-Anleitungen für ${ex}: Konto eröffnen, Futures-Wallet aufladen, API-Schlüssel erstellen und mit dem RealColibri-Handelsalgorithmus verbinden.`,
    },
    it: {
        title: `Guide ${ex} — collega il tuo conto a RealColibri` + SUFFIX,
        description: `Guide passo passo per ${ex}: apri un conto, finanzia il wallet futures, crea le chiavi API e collegale all'algoritmo di trading RealColibri.`,
    },
    hy: {
        title: `${ex} ուղեցույցներ — միացրեք ձեր հաշիվը RealColibri-ին` + SUFFIX,
        description: `${ex}-ի քայլ առ քայլ ուղեցույցներ․ բացեք հաշիվ, համալրեք ֆյուչերսային դրամապանակը, ստեղծեք API բանալիներ և միացրեք դրանք RealColibri առևտրային ալգորիթմին։`,
    },
})

export const SEO_TEXTS: SeoTexts = {
    ...STATIC,
    guidesBinance: exchangeGuides('Binance'),
    guidesBybit: exchangeGuides('Bybit'),
    guidesOkx: exchangeGuides('OKX'),
}

/** Для закрытых страниц (кабинет, админка): общий заголовок. */
export const PRIVATE_TITLE = 'RealColibri'
